import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, PencilLine, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { makeCommandId } from '../components/InvoiceApprovalReview';
import { correctInvoice } from './api';
import { readableErrors } from './invoiceStatus';

const HEADER = [
  ['supplier_name', 'Supplier', (inv) => inv.supplier_name],
  ['invoice_no', 'Invoice number', (inv) => inv.invoice_no],
  ['date', 'Date (YYYY-MM-DD)', (inv) => inv.date],
  ['invoice_total', 'Total', (inv) => inv.totals?.invoice_total],
  ['vat_total', 'VAT', (inv) => inv.totals?.vat_total],
];
const LINE = [
  ['raw_desc', 'Description', (ln) => ln.raw_desc ?? ln.description],
  ['qty', 'Qty', (ln) => ln.qty],
  // G-IN-6: what the line is counted in, and how much one pack holds ("1kg", "6 x 800g"): price and stock follow it.
  ['unit', 'Unit', (ln) => ln.unit],
  ['pack_size', 'Pack size', (ln) => ln.pack_size],
  ['unit_price', 'Unit price', (ln) => ln.unit_price ?? ln.unit_cost],
  ['line_total', 'Line total', (ln) => ln.line_total],
];
/** Per line, optional: what dates and traces the lot this line becomes (T3). Typed here, kept as before/after. */
const LINE_LOT = [
  ['use_by', 'Use by', (ln) => ln.use_by, 'date'],
  ['supplier_lot_ref', 'Supplier lot', (ln) => ln.supplier_lot_ref, 'text'],
];
const LINE_FIELDS = [...LINE, ...LINE_LOT].map(([field]) => field);
export const EDITABLE_STATES = ['needs_review', 'awaiting_approval'];

/** A held invoice stores a missing figure as the text "None": show it as empty. */
const shown = (v) => (v === null || v === undefined || v === 'None' ? '' : String(v));
const key = (field, idx) => (idx === null ? field : `${field}:${idx}`);

/** What was read, as the values the form starts from. `site` is the invoice's site (moved with a correction). */
export function readValues(invoice) {
  const out = {};
  HEADER.forEach(([field, , get]) => { out[key(field, null)] = shown(get(invoice)); });
  out.site = shown(invoice.location_id);
  (invoice.lines || []).forEach((ln, i) => [...LINE, ...LINE_LOT].forEach(([field, , get]) => { out[key(field, i)] = shown(get(ln)); }));
  return out;
}

/** Only what the person changed, in the API's shape. */
export function changedCorrections(before, after) {
  return Object.keys(after).filter((k) => (after[k] || '').trim() !== (before[k] || '').trim()).map((k) => {
    const [field, idx] = k.split(':');
    return idx === undefined ? { field, value: after[k].trim() } : { field, line_index: Number(idx), value: after[k].trim() };
  });
}

/**
 * Everything one save sends (backend/invoice_corrections.py): the changed values, then each added line as `add_line`
 * followed by its figures at the index it will take (after the invoice's own lines), then the removed lines. The server
 * reads every index as the lines were before any removal, so removals go last; an edit to a removed line is not sent.
 */
export function buildCorrections({ original, values, added = [], removed = [], lineCount = 0 }) {
  const edits = changedCorrections(original, values).filter((c) => c.line_index === undefined || !removed.includes(c.line_index));
  const adds = added.flatMap((row, k) => [
    { field: 'add_line', value: (row.raw_desc || '').trim() },
    ...LINE_FIELDS.filter((f) => f !== 'raw_desc' && (row[f] || '').trim())
      .map((f) => ({ field: f, line_index: lineCount + k, value: row[f].trim() })),
  ]);
  const removals = [...removed].sort((a, b) => a - b).map((i) => ({ field: 'remove_line', line_index: i }));
  return [...edits, ...adds, ...removals];
}

/**
 * G-LOT-3, warnings only (never a block): a typed use-by before the delivery (else the invoice date), and a goods-in
 * receipt whose own use-by the lot takes instead (backend/invoice_lot_expiry.py review_expiry gives the facts).
 */
export function expiryWarnings(typed, facts, lineIndex) {
  const day = (typed || '').trim();
  if (!day || !facts) return [];
  const out = [];
  const against = facts.delivery_date || facts.invoice_date;
  if (against && day < against) {
    out.push({ code: 'use_by_before_delivery',
      text: facts.delivery_date ? `Use by ${day} is before the delivery (${against}).`
        : `Use by ${day} is before the invoice date (${against}); no delivery is on record.` });
  }
  const receipt = (facts.lines || []).find((ln) => ln.line_index === lineIndex)?.receipt_use_by;
  if (receipt && receipt !== day) {
    out.push({ code: 'receipt_overrides_typed',
      text: `The goods-in receipt says use by ${receipt}: the stock lot takes that date, not the one typed here.` });
  }
  return out;
}

const blankRow = (id) => ({ id, ...Object.fromEntries(LINE_FIELDS.map((f) => [f, ''])) });

/**
 * Correct what was read. Every change is kept on the invoice as before/after (who, when); a held reading that adds up
 * after the change is matched and moved on by the server. Open by default when the reading could not be trusted.
 * Before approval only: lines can be added and removed (a removed line stays in the history), and the invoice moved to
 * another of this person's sites (`site.choices` is already narrowed by the server).
 */
export default function CorrectReading({ invoice, startOpen = false, onChanged, site = null, lotExpiry = null }) {
  const [open, setOpen] = useState(startOpen);
  const original = useMemo(() => readValues(invoice), [invoice]);
  const [values, setValues] = useState(original);
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const cmd = useRef(null);
  const nextId = useRef(0);
  // A reload after a save renumbers the lines: start again from what is now stored.
  useEffect(() => { setValues(original); setAdded([]); setRemoved([]); }, [original]);

  if (!EDITABLE_STATES.includes(invoice.state)) return null;
  const lines = invoice.lines || [];
  const changes = buildCorrections({ original, values, added, removed, lineCount: lines.length });
  const undescribed = added.some((row) => !(row.raw_desc || '').trim());
  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setResult(null); };
  const setNew = (id, field) => (e) => {
    setAdded((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: e.target.value } : row))); setResult(null);
  };
  const toggleRemoved = (i) => { setRemoved((r) => (r.includes(i) ? r.filter((x) => x !== i) : [...r, i])); setResult(null); };
  const addRow = () => { nextId.current += 1; setAdded((rows) => [...rows, blankRow(nextId.current)]); setResult(null); };
  const siteChoices = site?.can_change ? site.choices || [] : [];
  const saved = (lotExpiry?.lines || []).filter((ln) => (ln.warnings || []).length);

  const save = async () => {
    setBusy(true); setError(null);
    if (!cmd.current) cmd.current = makeCommandId('inv-correct');
    try {
      const out = await correctInvoice(invoice.id, { command_id: cmd.current, corrections: changes });
      cmd.current = null;
      setResult(out);
      setAdded([]); setRemoved([]);
      await onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="space-y-2">
        {saved.length > 0 && (
          <ul className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-sm text-accent-800 space-y-1" data-testid="correct-lot-warnings">
            {saved.flatMap((ln) => expiryWarnings(ln.typed_use_by, lotExpiry, ln.line_index).map((w) => (
              <li key={`${ln.line_index}-${w.code}`}>Line {ln.line_index + 1}: {w.text.charAt(0).toLowerCase() + w.text.slice(1)}</li>
            )))}
          </ul>
        )}
        <button type="button" onClick={() => setOpen(true)} data-testid="correct-open"
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl border border-surface-border bg-surface text-ink hover:bg-surface-sunken">
          <PencilLine className="w-4 h-4" /> Correct what was read
        </button>
      </div>
    );
  }
  const input = 'w-full min-h-[40px] rounded-lg border border-surface-border bg-surface px-2 text-ink';
  const grid = 'grid grid-cols-2 sm:grid-cols-[2fr_0.8fr_0.8fr_1fr_1fr_1fr_1.2fr_1.2fr_auto] gap-2 items-end';
  const n = changes.length;
  const warn = (i, typed) => expiryWarnings(typed, lotExpiry || { invoice_date: invoice.date, lines: [] }, i);
  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-4" data-testid="correct-reading">
      <div>
        <h2 className="font-display text-lg text-ink">Correct what was read</h2>
        <p className="text-sm text-ink-muted">Change only what differs from the document. The original reading is kept beside your correction.
          Use by and Supplier lot are optional: type what the label on the delivery says, so the stock lot carries it (no use-by typed
          means the delivery receipt's date, then the item's shelf life, else it is listed as expiry unknown).
          A removed line is kept in the correction history. Once the invoice is approved it is changed by a void or a credit note instead.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {HEADER.map(([field, label]) => (
          <label key={field} className="text-sm text-ink-muted">{label}
            <input className={input} value={values[key(field, null)]} onChange={set(key(field, null))} data-testid={`correct-${field}`} />
          </label>
        ))}
        {siteChoices.length > 0 && (
          <label className="text-sm text-ink-muted">Site its stock goes to
            <select className={input} value={values.site} onChange={set('site')} data-testid="correct-site">
              {!values.site && <option value="">Choose a site…</option>}
              {siteChoices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {(lines.length > 0 || added.length > 0) && (
        <div className="space-y-2" data-testid="correct-lines">
          {lines.map((ln, i) => {
            const gone = removed.includes(i);
            const warnings = gone ? [] : warn(i, values[key('use_by', i)]);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={`space-y-1 ${gone ? 'opacity-50' : ''}`} data-testid={`correct-line-${i}`}>
                <div className={grid}>
                  {LINE.map(([field, label]) => (
                    <label key={field} className="text-xs text-ink-muted">{i === 0 ? label : <span className="sr-only">{label}</span>}
                      <input className={`${input} ${gone ? 'line-through' : ''}`} aria-label={`${label}, line ${i + 1}`} value={values[key(field, i)]}
                        onChange={set(key(field, i))} disabled={gone} data-testid={`correct-${field}-${i}`} />
                    </label>
                  ))}
                  {LINE_LOT.map(([field, label, , type]) => (
                    <label key={field} className="text-xs text-ink-muted">{i === 0 ? label : <span className="sr-only">{label}</span>}
                      <input className={input} type={type} aria-label={`${label}, line ${i + 1}`} value={values[key(field, i)]}
                        onChange={set(key(field, i))} disabled={gone} data-testid={`correct-${field}-${i}`} />
                    </label>
                  ))}
                  <button type="button" onClick={() => toggleRemoved(i)} data-testid={`correct-remove-${i}`}
                    aria-label={gone ? `Keep line ${i + 1}` : `Remove line ${i + 1}`}
                    className="inline-flex items-center justify-center gap-1 min-h-[40px] px-3 rounded-lg border border-surface-border text-sm text-ink-muted hover:bg-surface-sunken">
                    {gone ? <><RotateCcw className="w-4 h-4" /> Keep</> : <><Trash2 className="w-4 h-4" /> Remove</>}
                  </button>
                </div>
                {warnings.map((w) => (
                  <p key={w.code} className="flex items-center gap-1 text-xs text-accent-800" data-testid={
                    w.code === 'use_by_before_delivery' ? `correct-use_by-warning-${i}` : `correct-use_by-receipt-${i}`}>
                    <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" /> {w.text}
                  </p>
                ))}
              </div>
            );
          })}
          {added.map((row, k) => (
            <div key={row.id} className="space-y-1 rounded-lg bg-surface-sunken p-2" data-testid={`correct-new-line-${k}`}>
              <div className={grid}>
                {[...LINE, ...LINE_LOT].map(([field, label, , type]) => (
                  <label key={field} className="text-xs text-ink-muted">{label}
                    <input className={input} type={type || 'text'} aria-label={`${label}, added line ${k + 1}`} value={row[field]}
                      onChange={setNew(row.id, field)} data-testid={`correct-new-${field}-${k}`} />
                  </label>
                ))}
                <button type="button" onClick={() => setAdded((rows) => rows.filter((r) => r.id !== row.id))}
                  data-testid={`correct-new-remove-${k}`} aria-label={`Drop added line ${k + 1}`}
                  className="inline-flex items-center justify-center gap-1 min-h-[40px] px-3 rounded-lg border border-surface-border text-sm text-ink-muted">
                  <Trash2 className="w-4 h-4" /> Drop
                </button>
              </div>
              {!(row.raw_desc || '').trim() && (
                <p className="text-xs text-warn" data-testid={`correct-new-missing-${k}`}>An added line needs its description.</p>
              )}
              {warn(lines.length + k, row.use_by).filter((w) => w.code === 'use_by_before_delivery').map((w) => (
                <p key={w.code} className="flex items-center gap-1 text-xs text-accent-800" data-testid={`correct-new-use_by-warning-${k}`}>
                  <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" /> {w.text}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={addRow} data-testid="correct-add-line"
        className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">
        <Plus className="w-4 h-4" /> Add a line
      </button>
      {result && (
        <p className={result.errors?.length ? 'text-warn' : 'text-primary-700'} data-testid="correct-result">
          {result.applied} change{result.applied === 1 ? '' : 's'} saved.{' '}
          {result.errors?.length ? `Still to fix: ${readableErrors(result.errors)}.` : 'The figures add up now.'}
        </p>
      )}
      {error && <p className="text-danger" data-testid="correct-error">{error}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" disabled={busy || !n || undescribed} onClick={save} data-testid="correct-save"
          className="min-h-[48px] px-5 rounded-xl bg-primary-500 text-surface font-medium disabled:opacity-50">
          {busy ? 'Saving…' : n ? `Save ${n} correction${n === 1 ? '' : 's'}` : 'Save corrections'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setValues(original); setAdded([]); setRemoved([]); setResult(null); setError(null); }}
          data-testid="correct-cancel" className="min-h-[48px] px-5 rounded-xl border border-surface-border text-ink">Close</button>
      </div>
    </section>
  );
}
